import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getImpersonatedAgency, IMPERSONATION_EVENT } from "@/lib/admin-impersonation";

type AgencyRole = "admin" | "agent" | "booker" | "viewer" | null;

interface UseAgencyRoleResult {
  role: AgencyRole;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  loading: boolean;
  agencyId: string | null;
}

export function useAgencyRole(): UseAgencyRoleResult {
  const { user, loading: authLoading } = useAuth();
  const [impersonationVersion, setImpersonationVersion] = useState(0);
  const impersonatedAgencyId = getImpersonatedAgency()?.id ?? null;

  // Re-run when admin impersonation state changes (same tab or other tabs)
  useEffect(() => {
    const bump = () => setImpersonationVersion((v) => v + 1);
    window.addEventListener(IMPERSONATION_EVENT, bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener(IMPERSONATION_EVENT, bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["agency-role", user?.id ?? null, impersonatedAgencyId, impersonationVersion],
    enabled: !!user && !authLoading,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Retry transient network blips ("Failed to fetch") so a flaky connection
    // doesn't briefly bounce agency users out of /agency/*.
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    queryFn: async (): Promise<{ role: AgencyRole; agencyId: string | null; isPlatformAdmin: boolean }> => {
      if (!user) return { role: null, agencyId: null, isPlatformAdmin: false };

      // Platform admin always has full admin rights on any agency context
      const { data: isPlatformAdmin } = await supabase.rpc("has_role", {
        _user_id: user.id,
        _role: "admin",
      });

      const impersonated = getImpersonatedAgency();

      if (isPlatformAdmin) {
        let agencyId: string | null = impersonated?.id ?? null;
        if (!agencyId) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("agency_id")
            .eq("id", user.id)
            .maybeSingle();
          agencyId = profile?.agency_id ?? null;
        }
        return { role: "admin", agencyId, isPlatformAdmin: true };
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("agency_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.agency_id) {
        return { role: null, agencyId: null, isPlatformAdmin: false };
      }

      const { data: roleData, error } = await supabase.rpc("get_user_agency_role", {
        _user_id: user.id,
        _agency_id: profile.agency_id,
      });

      if (error) {
        console.error("Error fetching agency role:", error);
        return { role: null, agencyId: profile.agency_id, isPlatformAdmin: false };
      }

      return { role: (roleData as AgencyRole) || null, agencyId: profile.agency_id, isPlatformAdmin: false };
    },
  });

  const role = data?.role ?? null;
  const agencyId = data?.agencyId ?? null;
  const isPlatformAdmin = data?.isPlatformAdmin ?? false;

  return {
    role,
    isAdmin: role === "admin",
    isPlatformAdmin,
    loading: authLoading || (!!user && isLoading),
    agencyId,
  };
}
